import React, { useEffect, useState, useCallback, useRef } from "react";
import { authStore } from "../lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import { Sparkles, Loader2, Save, Send, FileCheck2, Users, PenLine, X, Copy, ExternalLink, CheckCircle2, MessageSquareWarning, Layers, Eye, Ban, RotateCcw, Undo2 } from "lucide-react";
import { dateInputValue, fmtDT } from "../lib/usDate";
import { IntentContractCard } from "./crm/IntentContractCard";
import { useApp } from "../context/AppContext";
/* 【2026-10-30 输入侧时区提示】营销方案的「要求完成时间」按美国日历日写入 dueAt
   （后端 design_v3 把纯日期解析成洛杉矶 23:59:59，见 _attach_eng 同段口径），
   中国同事按北京日期填会让要求时间差一天。 */
import { UsDateHint } from "../pages/design/v3/UsDateHint";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const dl = (fileId) => `${BACKEND}/api/files/${fileId}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;

const STATUS = {
  draft: { zh: "待填写要求", en: "Draft", cls: "bg-slate-100 text-slate-500" },
  in_design: { zh: "设计中", en: "In design", cls: "bg-blue-100 text-blue-700" },
  submitted: { zh: "待发送客户", en: "Ready to send", cls: "bg-amber-100 text-amber-700" },
  sent: { zh: "已发客户", en: "Sent", cls: "bg-violet-100 text-violet-700" },
  client_agreed: { zh: "客户已同意", en: "Client agreed", cls: "bg-emerald-100 text-emerald-700" },
  client_revision: { zh: "客户提出修改", en: "Client revision", cls: "bg-red-100 text-red-700" },
};

export function ConceptDesignPanel({ api, zh, leadId, canEdit, layoutSlot }) {
  const { user } = useApp();
  const [cd, setCd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reqText, setReqText] = useState("");
  /*
   * 反馈 FB81e5a21831（2026-10-23）：
   * 销售在 CRM「概念方案设计」面板下达任务时看不到/填不了「要求完成时间」，
   * 但后端 concept_dispatch 与营销方案工单同步链路早已接收 dueAt。
   * 结果生产项目 ADU-0163 首次下达落成了 dueAt=null，设计师待办没有截止日期。
   *
   * 这里把工单里的日期作为单一事实源：已有 presign.dueAt 时预填，
   * 销售重新下达或返工时仍可在发送前调整，并随请求一起同步到设计师工作台。
   */
  const [dueAt, setDueAt] = useState("");
  const [roster, setRoster] = useState({ cn: [], us: [] });
  const [cnSel, setCnSel] = useState("");
  const [usSel, setUsSel] = useState("");
  const [dirty, setDirty] = useState(false);
  const [share, setShare] = useState(null);
  const [plans, setPlans] = useState([]);
  const [sendBusy, setSendBusy] = useState(false);
  const [replyTxt, setReplyTxt] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  /* 草稿自动保存（需求 SYS-005）
   * 痛点：设计要求输入框没有自动保存，页面一旦刷新/重挂载，未点「保存要求」的文字全部丢失。
   * 方案：① 用户停止输入 1.5 秒后自动 PATCH 保存（静默，不弹 toast，不打断输入）；
   *       ② 同时把草稿写 localStorage 兜底——即使请求失败、断网或浏览器直接刷新，
   *          下次进入同一条客户时也能恢复；真正保存成功后清掉本地草稿。
   * 取舍：自动保存只写 requirements 字段，不动阶段/派发等状态，避免静默改动业务状态。 */
  const draftKey = `concept.req.draft.${leadId}`;
  const savedReqRef = useRef("");                       // 服务端已确认保存的内容，用于判断是否还有未保存改动
  const autoSaveTimer = useRef(null);
  const [autoSaving, setAutoSaving] = useState(false);

  const toggleHist = async () => {
    if (!histOpen && hist === null) {
      try { const { data } = await api.get(`/crm/leads/${leadId}/proposal-package/history`); setHist(data); }
      catch { setHist({ history: [], opens: 0 }); }
    }
    setHistOpen((v) => !v);
  };

  const replySms = async () => {
    if (!replyTxt.trim()) return;
    setReplyBusy(true);
    try {
      await api.post(`/crm/leads/${leadId}/preference-reply-sms`, { text: replyTxt.trim() });
      toast.success(zh ? "已短信回复客户" : "SMS reply sent");
      setReplyTxt("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "回复失败" : "Failed")); }
    setReplyBusy(false);
  };

  const sendPackage = async () => {
    setSendBusy(true);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/proposal-package/send`);
      const parts = [
        data.sms ? (zh ? "短信已发" : "SMS sent") : (data.noPhone ? (zh ? "无手机号(短信未发)" : "no phone") : (zh ? "短信发送失败" : "SMS failed")),
        data.email ? (zh ? "邮件已发" : "Email sent") : (data.noEmail ? (zh ? "无邮箱(邮件未发)" : "no email") : (zh ? "邮件发送失败" : "Email failed")),
      ];
      if (data.link) { try { await navigator.clipboard.writeText(data.link); parts.push(zh ? "查看链接已复制" : "link copied"); } catch { /* */ } }
      toast.success(parts.join(" · "));
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Failed")); }
    setSendBusy(false);
  };

  useEffect(() => {
    api.get(`/crm/leads/${leadId}/layout-plans`).then(({ data }) => setPlans(data || [])).catch(() => setPlans([]));
  }, [api, leadId]);

  const pullLayout = () => {
    if (!plans.length) return;
    const lines = plans.map((p) => {
      const notes = (p.ownerNotes || []).map((n) => n.text).filter(Boolean);
      const sf = p.aduSf ? ` · ${Number(p.aduSf).toLocaleString()} sf` : "";
      return `- ${p.name}${sf}${notes.length ? (zh ? `｜业主批注：${notes.join("；")}` : ` | owner notes: ${notes.join("; ")}`) : ""}`;
    });
    const block = `${zh ? "【设计部布局方案要点】" : "[Design layout plans]"}\n${lines.join("\n")}`;
    // 带入也是「修改内容」，同样落到本地草稿，防止带入后立刻刷新又丢（SYS-005）
    setReqText((v) => {
      const next = (v ? v.trimEnd() + "\n\n" : "") + block;
      rememberDraft(next);
      return next;
    });
    setDirty(true);
    toast.success(zh ? "已带入布局方案要点，请检查并保存" : "Layout points added — review & save");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/crm/leads/${leadId}/concept-design`);
      if (data.exists) {
        setCd(data);
        const server = data.requirements || data.requirementsCn || "";
        /* SYS-005：如果本地还留着未保存草稿（上次刷新/崩溃前打的字），优先恢复草稿，
           并把 dirty 置为 true 提示用户「还没保存」，避免服务端旧值把草稿盖掉。
           草稿与服务端内容一致时视为已保存，直接清掉本地草稿。 */
        let draft = "";
        try { draft = localStorage.getItem(draftKey) || ""; } catch { /* 隐私模式禁用存储 → 跳过 */ }
        if (draft && draft !== server) {
          setReqText(draft);
          setDirty(true);
        } else {
          setReqText(server);
          setDirty(false);
          if (draft) { try { localStorage.removeItem(draftKey); } catch { /* ignore */ } }
        }
        savedReqRef.current = server;
        setCnSel(data.cnAssignee || "");
        setUsSel(data.usAssignee || "");
        // 【2026-10-23】预填优先取「销售自己提的下一次要求完成时间」
        // （`presign.improveDueAt`），取不到才回落 `dueAt`。
        //
        // 原因：销售点「改为待改进」时填的是 improveDueAt，而这里原来只读 dueAt。
        // 生产 ADU-0150 这类单的 dueAt 本来就是空的（首次下达没填），于是销售
        // 明明刚提过 10-24，进面板却看到一个空日期框，会以为没保存成功。
        // 口径与设计侧 `order.improveDueAt || order.dueAt` 一致，三处显示同一事实。
        /* 【2026-10-29 时区统一】日期框初值改走 dateInputValue（洛杉矶日历日）。
           原来 `String(x).slice(0,10)` 取的是 UTC 日：`dueAt` 是纯日期时无影响，
           但 `improveDueAt` 若存成带时区的瞬间，中国同事看到的初值会与列表差一天。 */
        setDueAt(dateInputValue(data.presign?.improveDueAt || data.presign?.dueAt || ""));
      }
      else setCd(null);
    } catch { setCd(null); }
    setLoading(false);
  }, [api, leadId, draftKey]);
  useEffect(() => { load(); }, [load]);

  /* 草稿落本地（SYS-005）：输入即写 localStorage（同步、极快），刷新后由上面的 load 恢复。
     localStorage 写入失败（隐私模式/配额满）静默忽略，不影响正常输入与保存。 */
  const rememberDraft = (text) => {
    try {
      if (text?.trim()) localStorage.setItem(draftKey, text);
      else localStorage.removeItem(draftKey);
    } catch { /* ignore */ }
  };

  /* 停止输入 1.5s 自动保存（SYS-005）：静默 PATCH，不弹提示、不做 loading 遮罩，
     避免打断正在打字的人；成功后清本地草稿。失败时保留草稿，等下一轮输入或手动保存再试。 */
  const autoSave = useCallback(async (text) => {
    if (!canEdit || !cd) return;
    if (text === savedReqRef.current) return;
    setAutoSaving(true);
    try {
      await api.patch(`/crm/leads/${leadId}/concept-design`, { requirements: text });
      savedReqRef.current = text;
      setDirty(false);
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    } catch { /* 静默失败：草稿仍在本地，下次输入/手动保存会再试 */ }
    setAutoSaving(false);
  }, [api, leadId, canEdit, cd, draftKey]);

  // 自动保存定时器：每次输入重置 1.5s 计时，停止输入后才发请求，避免每个字符打一次接口
  useEffect(() => {
    if (!dirty || !canEdit || !cd) return undefined;
    const t = setTimeout(() => { autoSave(reqText); }, 1500);
    return () => clearTimeout(t);
  }, [reqText, dirty, canEdit, cd, autoSave]);

  // 离开页面前兜底：还有未保存内容就提示浏览器（配合自动保存，通常已保存成功不会弹）
  useEffect(() => {
    const h = (e) => {
      if (!dirty) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  useEffect(() => {
    if (!canEdit) return;
    api.get(`/crm/leads/${leadId}/concept-design/roster`).then(({ data }) => setRoster(data || { cn: [], us: [] })).catch(() => {});
  }, [api, leadId, canEdit]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/concept-design/generate`, {});
      setCd(data); setReqText(data.requirements || ""); setCnSel(data.cnAssignee || ""); setUsSel(data.usAssignee || ""); setDirty(false);
      // 同 load()：优先销售自己提的 improveDueAt（见上方注释），再回落 dueAt。
      setDueAt(dateInputValue(data.presign?.improveDueAt || data.presign?.dueAt || ""));
      toast.success(zh ? "任务已创建，请填写设计要求并选择中/美设计师" : "Task created — write the brief & pick designers");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "创建失败" : "Failed")); }
    setBusy(false);
  };

  const saveReq = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/crm/leads/${leadId}/concept-design`, { requirements: reqText });
      setCd(data); setDirty(false);
      savedReqRef.current = reqText;
      // 手动保存成功 → 本地草稿已完成使命，清掉避免下次误恢复旧内容（SYS-005）
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      toast.success(zh ? "设计要求已保存" : "Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
    setBusy(false);
  };

  const dispatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/concept-design/dispatch`, {
        cnAssignee: cnSel,
        usAssignee: usSel,
        requirements: reqText,
        dueAt,
      });
      setCd(data); setDirty(false);
      setDueAt(dateInputValue(data.presign?.dueAt || dueAt));
      toast.success(zh ? "设计要求已发送给中/美设计师，已加入其工作台待办" : "Dispatched to CN & US designers");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Failed")); }
    setBusy(false);
  };

  /* 签约前营销方案「已关闭」状态（2026-10-22 用户需求：「销售端要对应的显示并且能够恢复哦」）。
   *
   * 背景：设计部把客户不做了的工单关掉之后，销售在 CRM 客户详情里仍然看到旧的
   * concept_designs.status（in_design / submitted），于是以为方案还在做，
   * 甚至继续催设计师 —— 两边对同一件事的判断不一致。
   * 关闭状态落在 `concept_designs.presign.status`（GET 接口已随 clean(doc) 一并返回），
   * 这里直接读它，不改动顶层 status：顶层 status 描述的是「概念方案走到哪一步」，
   * 关闭是工单层面的收尾，两者语义不同，混写会让其它读 status 的地方（CRM 列表、
   * 提醒、报表）出现新的歧义。
   *
   * 恢复：走与设计部列表同一个接口 `/design/presign/orders/{lid}/restore`，
   * 恢复到关闭前的状态（后端 closedFromStatus 决定）。销售与设计师都能恢复，
   * 权限由后端判定，前端只按 presign 里是否已关闭决定显示哪个按钮。
   */
  const presignClosed = cd?.presign?.status === "closed";
  const restorePresign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/design/presign/orders/${leadId}/restore`);
      toast.success(zh
        ? `已恢复签约前营销方案（${data.order?.statusLabel || "待制作"}）`
        : "Pre-contract work order restored");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "恢复失败" : "Restore failed"));
    }
    setBusy(false);
  };

  const sendClient = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/concept-design/send`, {});
      const url = `${window.location.origin}/concept/${data.token}`;
      setShare({ ...data, url }); await load();
      toast.success(zh ? "已生成客户链接" : "Client link ready");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Failed")); }
    setBusy(false);
  };

  const packagePdf = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/concept-design/package`);
      toast.success(zh ? "已打包并存入客户文件区（概念方案确认）" : "Packaged to client files");
      if (data.file?.fileId) window.open(dl(data.file.fileId), "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "打包失败" : "Failed")); }
    setBusy(false);
  };

  if (loading) return <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-400" data-testid="concept-panel-loading">…</div>;

  const st = cd ? (STATUS[cd.status] || STATUS.in_design) : null;
  const cr = cd?.clientResponse;
  // 签约前工单的提交版本与设计部详情同读 finalHistory。CRM 上方 finalFiles
  // 展示当前版的完整附件，旧版在下方逐版列出，销售回看时不会只剩一个主文件。
  const previousPresignVersions = cd?.presign ? (cd.finalHistory || []).slice(0, -1).reverse() : [];

  return (
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white p-4 space-y-3" data-testid="concept-design-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="h-4 w-4 text-emerald-600" />
        <h4 className="text-sm font-semibold text-slate-800">{zh ? "概念方案设计（中/美设计配合）" : "Concept Design"}</h4>
        {st && <span className={`text-[11px] px-2 py-0.5 rounded-full ${st.cls}`} data-testid="concept-status">{zh ? st.zh : st.en}</span>}
        {/* 已关闭徽标（2026-10-22）：销售在 CRM 里一眼看出「这单客户不做了」，
            不必去设计部列表里查。徽标与恢复按钮放在最前，压过顶层 status 的显示，
            否则「设计中」和「已关闭」同时出现会让人以为还在做。 */}
        {presignClosed && (
          <span className="text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-slate-200 text-slate-700 font-semibold"
            data-testid="concept-presign-closed-badge"
            title={cd?.presign?.closedReason ? `${zh ? "关闭理由" : "Reason"}: ${cd.presign.closedReason}` : ""}>
            <Ban className="h-3 w-3" />{zh ? "签约前方案已关闭" : "Pre-contract closed"}
          </span>
        )}
        {presignClosed && canEdit && (
          <button onClick={restorePresign} disabled={busy} data-testid="concept-presign-restore-btn"
            className="inline-flex items-center gap-1 text-[11px] text-violet-700 border border-violet-300 bg-violet-50 rounded-lg px-2 py-1 hover:bg-violet-100 disabled:opacity-50">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
            {zh ? "恢复" : "Restore"}
          </button>
        )}
        {cd?.shareToken && (
          <span className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${cd.viewCount ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-400"}`} data-testid="concept-view-stats"
            title={cd.lastViewedAt ? (zh ? `最近查看 ${fmtDT(cd.lastViewedAt)}` : `Last viewed ${fmtDT(cd.lastViewedAt)}`) : ""}>
            <Eye className="h-3 w-3" />
            {cd.viewCount ? (zh ? `业主已打开 ${cd.viewCount} 次` : `Viewed ${cd.viewCount}×`) : (zh ? "业主尚未打开" : "Not viewed")}
            {cd.lastViewedAt && <span className="text-[10px] opacity-70">· {String(cd.lastViewedAt).slice(5, 16).replace("T", " ")}</span>}
          </span>
        )}
        {cd && (cd.cnAssigneeName || cd.usAssigneeName) && (
          <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Users className="h-3 w-3" />
            {zh ? "中" : "CN"}:{cd.cnAssigneeName || "—"} · {zh ? "美" : "US"}:{cd.usAssigneeName || "—"}</span>
        )}
        {cd?.clientPreferredPlanId && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold" data-testid="concept-preferred-badge"
            title={cd.clientPreferredNote || ""}>
            {zh ? "客户偏好: " : "Client favourite: "}{cd.clientPreferredPlanName || cd.clientPreferredPlanId}
          </span>
        )}
        {cd?.clientPreferredNote && (
          <span className="text-[11px] text-slate-500 italic truncate max-w-[260px]" data-testid="concept-preferred-note">“{cd.clientPreferredNote}”</span>
        )}
        <a href={`${BACKEND}/api/crm/leads/${leadId}/proposal-package/pdf?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`}
          target="_blank" rel="noreferrer" data-testid="proposal-package-btn"
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-emerald-700 border border-emerald-300 rounded-lg px-2.5 py-1.5 hover:bg-emerald-50">
          <FileCheck2 className="h-3.5 w-3.5" />{zh ? "初步方案包 PDF" : "Proposal PDF"}
        </a>
        {/* 已关闭的工单不再给「发送客户」（2026-10-22）：
            客户已经不做了，还能把方案包发给客户是矛盾的，销售误点会把一个
            已收尾的工单重新推给客户。要发就先点「恢复」。 */}
        {canEdit && !presignClosed && (
          <button onClick={sendPackage} disabled={sendBusy} data-testid="proposal-package-send-btn"
            className="inline-flex items-center gap-1 text-[11px] text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-2.5 py-1.5 disabled:opacity-50">
            {sendBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}{zh ? "发送客户" : "Send to client"}
          </button>
        )}
        <button onClick={toggleHist} data-testid="proposal-package-history-btn"
          className="inline-flex items-center gap-1 text-[11px] text-slate-500 border border-slate-200 rounded-lg px-2 py-1.5 hover:bg-slate-50">
          {zh ? "版本历史" : "History"}
        </button>
      </div>
      {/* 已关闭说明条（2026-10-22）：把「谁在什么时候因为什么关的」直接摊开。
          销售最常见的疑问是「这单怎么不做了 / 是不是搞错了」，写清理由与操作人
          才能自己判断要不要点恢复。附件（若有）同处可下载，不必回设计部页面找。 */}
      {presignClosed && (
        <div className="rounded-lg border border-slate-300 bg-slate-100/70 px-2.5 py-2 text-[11px] leading-5 text-slate-700"
          data-testid="concept-presign-closed-note">
          <p className="inline-flex items-center gap-1.5 font-semibold text-slate-800">
            <Ban className="h-3.5 w-3.5" />{zh ? "签约前营销方案已关闭" : "Pre-contract work order closed"}
          </p>
          {cd?.presign?.closedReason && (
            <p className="mt-0.5">{zh ? "关闭理由" : "Reason"}：{cd.presign.closedReason}</p>
          )}
          <p className="text-slate-500">
            {cd?.presign?.closedBy || "—"}
            {cd?.presign?.closedAt ? ` · ${fmtDT(cd.presign.closedAt)}` : ""}
          </p>
          {(cd?.presign?.closedFiles || []).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="concept-presign-closed-files">
              {(cd.presign.closedFiles || []).map((f, i) => (
                <a key={f.fileId || i} href={dl(f.fileId)} target="_blank" rel="noreferrer"
                  data-testid={`concept-presign-closed-file-${i}`}
                  className="inline-flex max-w-full items-center gap-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] text-slate-600 hover:text-violet-700">
                  <FileCheck2 className="h-2.5 w-2.5 shrink-0" />
                  <span className="truncate max-w-[160px]">{f.filename || "附件"}</span>
                </a>
              ))}
            </div>
          )}
          <p className="mt-0.5 text-slate-500">
            {zh ? "客户重新需要方案时点上方「恢复」，会回到关闭前的状态。" : "Click Restore above to reopen it."}
          </p>
        </div>
      )}
      {histOpen && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5 space-y-1" data-testid="proposal-package-history">
          <p className="text-[11px] text-slate-500 font-semibold">
            {zh ? "方案包版本历史" : "Proposal versions"}
            {hist?.opens ? <span className="ml-2 text-emerald-600">{zh ? `客户已查看 ${hist.opens} 次` : `viewed ${hist.opens}×`}{hist.lastOpenAt ? `（${zh ? "最近" : "last"} ${fmtDT(hist.lastOpenAt)}）` : ""}</span> : null}
          </p>
          {(hist?.history || []).length === 0 && <p className="text-[11px] text-slate-400">{zh ? "尚未发送过方案包" : "No versions yet"}</p>}
          {(hist?.history || []).slice(0, 8).map((h, i) => (
            <div key={`${h.fileId}-${i}`} className="flex items-center justify-between gap-2 text-[11px] text-slate-600" data-testid={`proposal-version-${i}`}>
              <span className="truncate">{fmtDT(h.at)} · {h.by}{h.plans ? ` · ${h.plans}${zh ? " 图" : " plans"}` : ""}</span>
              <span className="shrink-0 flex items-center gap-2">
                <button onClick={() => setPdfPreview((p) => p?.fileId === h.fileId ? null : { fileId: h.fileId, url: `${dl(h.fileId)}#toolbar=1`, label: `${fmtDT(h.at)} · ${h.by}` })}
                  data-testid={`proposal-version-preview-${i}`} className="text-emerald-700 underline">{zh ? "预览" : "Preview"}</button>
                <a className="text-indigo-600 underline" href={dl(h.fileId)} target="_blank" rel="noreferrer">{zh ? "下载" : "Download"}</a>
              </span>
            </div>
          ))}
          {pdfPreview && (
            <div className="rounded-lg border border-slate-200 overflow-hidden bg-white" data-testid="proposal-pdf-preview">
              <div className="flex items-center justify-between bg-slate-100 px-2 py-1">
                <span className="text-[11px] text-slate-500 truncate">{zh ? "在线预览 · " : "Preview · "}{pdfPreview.label}</span>
                <button onClick={() => setPdfPreview(null)} data-testid="proposal-pdf-preview-close" className="text-slate-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
              </div>
              <iframe src={pdfPreview.url} title="proposal-pdf" className="w-full border-0" style={{ height: 460 }} />
            </div>
          )}
        </div>
      )}
      {cd?.clientPreferredNote && canEdit && (
        <div className="flex items-center gap-2" data-testid="preference-reply-box">
          <input value={replyTxt} onChange={(e) => setReplyTxt(e.target.value)} maxLength={300}
            placeholder={zh ? "短信回复客户留言…" : "SMS reply to client note…"}
            data-testid="preference-reply-input"
            className="flex-1 text-[12px] border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-300" />
          <button onClick={replySms} disabled={replyBusy || !replyTxt.trim()} data-testid="preference-reply-send"
            className="shrink-0 inline-flex items-center gap-1 text-[11px] text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-2.5 py-1.5 disabled:opacity-50">
            {replyBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}{zh ? "短信回复" : "Reply SMS"}
          </button>
        </div>
      )}
      {(cd?.preferenceReplies || []).length > 0 && (
        <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-2 space-y-1" data-testid="preference-reply-history">
          <p className="text-[10px] text-slate-400 font-semibold">{zh ? `短信回复记录（销售 → 客户，共 ${cd.preferenceReplies.length} 条）` : `SMS replies to client (${cd.preferenceReplies.length})`}</p>
          {cd.preferenceReplies.slice(-5).reverse().map((r, i) => (
            <p key={i} className="text-[11px] text-slate-600" data-testid={`preference-reply-item-${i}`}>
              <span className="text-slate-400">{r.at ? fmtDT(r.at) : ""} · {r.by}：</span>{r.text}
            </p>
          ))}
        </div>
      )}

      {layoutSlot && (
        <div className="pb-3 border-b border-emerald-100" data-testid="concept-layout-slot">
          <p className="text-[11px] font-semibold text-slate-500 mb-2">{zh ? "设计部布局方案" : "Design layout plans"}</p>
          {layoutSlot}
        </div>
      )}

      {!cd ? (
        <div className="text-center py-4">
          <p className="text-xs text-slate-400 mb-2">{zh ? "创建概念方案设计任务：销售填写设计要求，选择中国方案设计师与美国配合设计师后发送至其工作台。" : "Create concept-design task: sales writes the requirements, picks CN & US designers, then dispatches to their workbench."}</p>
          <Button size="sm" disabled={!canEdit || busy} onClick={generate} data-testid="concept-generate-btn" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FileCheck2 className="h-3.5 w-3.5 mr-1" />}{zh ? "创建概念方案设计任务" : "Create task"}
          </Button>
          {!canEdit && <p className="text-[11px] text-slate-400 mt-2">{zh ? "仅负责销售/经理可生成" : "Sales/managers only"}</p>}
        </div>
      ) : (
        <>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">{zh ? "设计要求（销售填写 · 共享给中国方案设计师与美国配合设计师）" : "Design requirements (shared to CN & US designers)"}</label>
            {/* SYS-005：输入即写本地草稿；停止输入 1.5s 静默自动保存到服务端，刷新/重挂载不再丢字。
                右侧状态用来告诉用户「已自动保存」，省去对「保存要求」按钮的依赖。 */}
            <textarea data-testid="concept-req" value={reqText} disabled={!canEdit}
              onChange={(e) => { setReqText(e.target.value); setDirty(true); rememberDraft(e.target.value); }}
              rows={7} placeholder={zh ? "请填写设计要求：产出内容、面积/房型、摆位朝向、客户偏好、法规注意点等…" : "Deliverables, sizes, layout, client preferences, code notes…"}
              className="mt-1 w-full text-xs rounded-lg border border-slate-200 p-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:bg-slate-50" />
            <div className="flex items-center gap-2 mt-1 text-[10px]" data-testid="concept-req-status">
              {autoSaving ? (
                <span className="text-slate-400 inline-flex items-center gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" />{zh ? "自动保存中…" : "Auto-saving…"}</span>
              ) : dirty ? (
                <span className="text-amber-600">{zh ? "有未保存内容 · 停止输入后会自动保存；直接刷新也不会丢失" : "Unsaved · auto-saves after you stop typing; a refresh won't lose it"}</span>
              ) : reqText ? (
                <span className="text-emerald-600">{zh ? "已保存" : "Saved"}</span>
              ) : null}
            </div>
          </div>

          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={busy || !dirty} onClick={saveReq} data-testid="concept-save-btn" className="h-8 text-xs bg-slate-700 hover:bg-slate-800 text-white">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}{zh ? "保存要求" : "Save"}
              </Button>
              {plans.length > 0 && (
                <Button size="sm" variant="outline" disabled={busy} onClick={pullLayout} data-testid="concept-pull-layout-btn" className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50">
                  <Layers className="h-3.5 w-3.5 mr-1" />{zh ? "带入布局方案要点" : "Pull layout points"}
                </Button>
              )}
            </div>
          )}

          {/* 待改进要求回显（2026-10-23 用户需求）。
              需求原话：「销售处需要显示时间那个问题..也需要哦...就是他自己提的时间也需要写到销售处这边回显」。

              修改原因：销售在「签约前营销方案」列表或详情点「改为待改进」时填的
              改进需求 + 下一次要求完成时间，后端早已写进 `presign.improveRequirement /
              improveDueAt / improveBy / improveAt`，设计侧（详情「改进要求记录」、
              待改进提示卡）与工单列表都能看到，**唯独销售自己进这个 CRM 面板时看不到** ——
              他只能看到上面那个可编辑的日期框，且那个框读的是 `presign.dueAt`，
              不是他提的 `improveDueAt`。于是销售会怀疑「我填的时间是不是没存上」。

              最终效果：销售一进面板就看到「本单已退回待改进 · 改进需求 · 下一次要求完成时间
              · 提出人 / 提出时间」，与他当初在列表弹窗里填的完全一致；
              同时说明这时该做什么（等设计师交新版），避免他再去点重复的退回。

              关键取舍：
                · 只读展示，不再给第二个「改改进要求」入口 —— 口径同设计侧详情那块
                  「这是留痕，不提供编辑入口」，要补新意见等设计师交完版再退回一轮；
                · 放在下达区**上方**：它比「发送设计要求」更贴近销售当下的疑问；
                · 时间用 `improveDueAt`，取不到才回落 `dueAt`（历史单可能只有后者），
                  与设计侧 `order.improveDueAt || order.dueAt` 同一口径，三处不会各说各话。 */}
          {cd?.presign?.status === "rework" && (cd?.presign?.improveRequirement || cd?.presign?.improveDueAt) && (
            <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-2.5" data-testid="concept-presign-improve-box">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold text-orange-800">
                <Undo2 className="h-3.5 w-3.5" />
                {zh ? "本单已退回待改进（等设计师交新版）" : "Sent back for revision (awaiting new version)"}
              </p>
              <p className="mt-1 whitespace-pre-wrap break-words text-[11px] leading-5 text-slate-700">
                <span className="text-slate-400">{zh ? "改进需求" : "Revision request"}：</span>
                {cd.presign.improveRequirement || cd.presign.reworkReason || "—"}
              </p>
              <p className="text-[11px] text-slate-700">
                <span className="text-slate-400">{zh ? "下一次要求完成时间" : "Next due"}：</span>
                <span className="font-semibold tabular-nums text-orange-800" data-testid="concept-presign-improve-due">
                  {cd.presign.improveDueAt || cd.presign.dueAt || "—"}
                </span>
                {(cd.presign.improveBy || cd.presign.improveAt) && (
                  <span className="ml-2 text-slate-400">
                    {cd.presign.improveBy || ""}
                    {cd.presign.improveAt ? ` · ${fmtDT(cd.presign.improveAt)}` : ""}
                  </span>
                )}
              </p>
              {(cd.presign.improveFiles || []).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1.5 border-t border-orange-100 pt-1" data-testid="concept-presign-improve-files">
                  {(cd.presign.improveFiles || []).map((f, i) => (
                    <a key={f.fileId || i} href={dl(f.fileId)} target="_blank" rel="noreferrer"
                      data-testid={`concept-presign-improve-file-${i}`}
                      className="inline-flex max-w-full items-center gap-1 rounded border border-orange-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-600 hover:text-violet-700">
                      <FileCheck2 className="h-2.5 w-2.5 shrink-0" />
                      <span className="truncate max-w-[160px]">{f.filename || "附件"}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {canEdit && !presignClosed && (
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-2.5 space-y-2" data-testid="concept-dispatch-box">
              <p className="text-[11px] font-semibold text-slate-500">{zh ? "发送设计要求给设计师（进入其工作台待办）" : "Dispatch to designers (workbench task)"}</p>
              <div className="flex flex-wrap items-center gap-2">
                <select value={cnSel} onChange={(e) => setCnSel(e.target.value)} data-testid="concept-dispatch-cn" className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white">
                  <option value="">{zh ? "选择中国方案设计师…" : "CN designer…"}</option>
                  {(roster.cn || []).map((r) => <option key={r.username} value={r.username}>{r.name}</option>)}
                </select>
                <select value={usSel} onChange={(e) => setUsSel(e.target.value)} data-testid="concept-dispatch-us" className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white">
                  <option value="">{zh ? "选择美国配合设计师…" : "US designer…"}</option>
                  {(roster.us || []).map((r) => <option key={r.username} value={r.username}>{r.name}</option>)}
                </select>
                {/* 反馈 FB81e5a21831：要求完成时间必须与任务同屏可填。
                    放在发送按钮前，销售不会在只选了设计师、没填日期时误发；
                    日期沿用营销方案工单的 YYYY-MM-DD 口径，后端直接写入 dueAt。 */}
                <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                  <span className="shrink-0">{zh ? "要求完成时间" : "Due date"}</span>
                  <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)}
                    disabled={!canEdit || busy} data-testid="concept-dispatch-due-at"
                    className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 disabled:bg-slate-50" />
                </label>
                {/* 【2026-10-30】要求完成时间按美国日历日；工具条横向空间紧，用 compact 形态。 */}
                <UsDateHint compact testid="concept-dispatch-due-at-tz" />
                <Button size="sm" disabled={busy || !reqText.trim() || !cnSel || !usSel || !dueAt} onClick={dispatch} data-testid="concept-dispatch-btn"
                  title={!reqText.trim() ? (zh ? "请先填写设计要求" : "Write requirements first") : (!cnSel || !usSel) ? (zh ? "请选择中/美设计师" : "Pick designers") : !dueAt ? (zh ? "请选择要求完成时间" : "Pick a due date") : ""}
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}{zh ? "发送设计要求" : "Dispatch"}
                </Button>
              </div>
              {cd.dispatchedAt && (
                <p className="text-[11px] text-emerald-600" data-testid="concept-dispatched-note">
                  ✓ {zh ? `已发送至设计师工作台 · ${fmtDT(cd.dispatchedAt)}${cd.dispatchedBy ? ` · ${cd.dispatchedBy}` : ""}` : `Dispatched ${fmtDT(cd.dispatchedAt)}`}
                </p>
              )}
            </div>
          )}

          {/* Final concept file + send */}
          <div className="pt-2 border-t border-slate-100 space-y-2">
            {cd.reviewStatus && cd.reviewStatus !== "pending" && (
              <p className={`text-[11px] inline-flex items-center gap-1 ${cd.reviewStatus === "approved" ? "text-emerald-600" : "text-red-600"}`} data-testid="concept-review-status">
                {cd.reviewStatus === "approved" ? (zh ? "✓ 设计经理已审核通过" : "✓ Approved by design manager") : (zh ? `⚠ 设计经理已退回${cd.reviewNote ? "：" + cd.reviewNote : ""}` : "⚠ Rejected by design manager")}
              </p>
            )}
            {(cd.finalFiles && cd.finalFiles.length) ? (
              <div data-testid="concept-final-files">
                {cd.presign && (cd.finalHistory || []).length > 0 && (
                  <p className="mb-1 text-[11px] font-semibold text-slate-600" data-testid="concept-current-version">
                    {zh ? `当前方案 v${cd.finalHistory.length} · ${cd.finalFiles.length} 个附件` : `Current v${cd.finalHistory.length} · ${cd.finalFiles.length} files`}
                  </p>
                )}
                <div className="flex flex-wrap gap-1.5 items-center">
                {cd.finalFiles.map((f, i) => (
                  <a key={f.fileId || i} href={dl(f.fileId)} target="_blank" rel="noreferrer" data-testid={`concept-final-file-${i}`} className="inline-flex items-center gap-1.5 text-xs text-emerald-700 border border-emerald-200 rounded-lg px-2.5 py-1.5 hover:bg-emerald-50">
                    <FileCheck2 className="h-3.5 w-3.5" />{f.label ? `${f.label}：` : ""}{f.filename}
                  </a>
                ))}
                {cd.importedToDesign?.pid && (
                  <a href={`/design/projects/${cd.importedToDesign.pid}?tab=disciplines&disc=${cd.importedToDesign.target || "floor_plan"}`} target="_blank" rel="noreferrer" data-testid="concept-imported-badge" className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100" title={zh ? "签约后已自动复制到设计项目「平面图」交付物作为参考方案 v0" : "Copied into the design project's Floor Plan deliverable as reference v0"}>
                    ✓ {zh ? `已导入设计项目 ${cd.importedToDesign.projectId || ""}（${cd.importedToDesign.count} 项）` : `In design project ${cd.importedToDesign.projectId || ""} (${cd.importedToDesign.count})`}
                  </a>
                )}
                </div>
              </div>
            ) : cd.finalFile ? (
              <a href={dl(cd.finalFile.fileId)} target="_blank" rel="noreferrer" data-testid="concept-final-file" className="inline-flex items-center gap-1.5 text-xs text-emerald-700 border border-emerald-200 rounded-lg px-2.5 py-1.5 hover:bg-emerald-50">
                <FileCheck2 className="h-3.5 w-3.5" />{zh ? "最终概念方案：" : "Final: "}{cd.finalFile.filename}
              </a>
            ) : (
              <p className="text-[11px] text-slate-400">{zh ? "等待设计师提交最终概念方案设计文件…" : "Awaiting designer's final file…"}</p>
            )}
            {previousPresignVersions.length > 0 && (
              <div className="space-y-2 border-t border-slate-100 pt-2" data-testid="concept-presign-previous-versions">
                <p className="text-[11px] font-semibold text-slate-600">{zh ? "历史方案附件" : "Previous proposal files"}</p>
                {previousPresignVersions.map((v, i) => (
                  <div key={v.version || i} className="space-y-1" data-testid={`concept-presign-version-${v.version || previousPresignVersions.length - i}`}>
                    <p className="text-[11px] text-slate-500">v{v.version || previousPresignVersions.length - i} · {v.submittedBy || "—"} · {v.files?.length || 0} {zh ? "个附件" : "files"}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(v.files || []).map((f, j) => (
                        <a key={f.fileId || j} href={dl(f.fileId)} target="_blank" rel="noreferrer"
                          data-testid={`concept-presign-version-file-${v.version || previousPresignVersions.length - i}-${j}`}
                          className="inline-flex max-w-full items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:text-violet-700 focus-visible:ring-2 focus-visible:ring-violet-400">
                          <FileCheck2 className="h-3 w-3 shrink-0" /><span className="max-w-[180px] truncate">{f.filename || "附件"}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* 已关闭同样不给「一键发送客户」：与顶部「发送客户」同一口径。 */}
            {canEdit && !presignClosed && (
              <Button size="sm" disabled={busy || !cd.finalFile} onClick={sendClient} data-testid="concept-send-btn" title={!cd.finalFile ? (zh ? "请先由设计师提交最终方案" : "Awaiting final file") : ""}
                className="h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-40">
                <Send className="h-3.5 w-3.5 mr-1" />{zh ? "一键发送客户（链接+二维码+短信/邮件）" : "Send to client"}
              </Button>
            )}
            {canEdit && cd.finalFile && (
              <Button size="sm" variant="outline" disabled={busy} onClick={packagePdf} data-testid="concept-package-btn"
                className="h-8 text-xs border-slate-300 text-slate-700 hover:bg-slate-50 ml-2">
                <FileCheck2 className="h-3.5 w-3.5 mr-1" />{zh ? "打包方案PDF（含签署存证）" : "Package PDF"}
              </Button>
            )}
            {cd.shareToken && (
              <Button size="sm" variant="outline" onClick={() => setShare({ url: `${window.location.origin}/concept/${cd.shareToken}` })} data-testid="concept-view-share-btn"
                className="h-8 text-xs border-violet-300 text-violet-700 hover:bg-violet-50 ml-2">
                <ExternalLink className="h-3.5 w-3.5 mr-1" />{zh ? "客户网页/二维码" : "Client page / QR"}
              </Button>
            )}
          </div>

          {/* Client response */}
          {cr && (
            <div className={`rounded-lg border p-3 ${cr.decision === "agree" ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="concept-client-response">
              <p className={`text-xs font-semibold flex items-center gap-1.5 ${cr.decision === "agree" ? "text-emerald-700" : "text-red-700"}`}>
                {cr.decision === "agree" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <MessageSquareWarning className="h-3.5 w-3.5" />}
                {cr.decision === "agree" ? (zh ? `客户 ${cr.signerName || ""} 已签字同意` : "Client agreed") : (zh ? "客户提出修改意见" : "Client requested changes")}
              </p>
              {cr.comment && <p className="text-xs text-slate-600 mt-1">{zh ? "意见：" : "Note: "}{cr.comment}</p>}
              {cr.decision === "agree" && <p className="text-[11px] text-emerald-600 mt-1" data-testid="concept-auto-pdf-note">{zh ? "✓ 已自动生成签署确认单 PDF 并存入客户文件区「概念方案确认」" : "✓ Signed PDF saved to client files"}</p>}
              <div className="flex gap-3 mt-2">
                {cr.signatureFileId && <a href={dl(cr.signatureFileId)} target="_blank" rel="noreferrer" data-testid="concept-signature" className="text-[11px] text-indigo-600 inline-flex items-center gap-1"><PenLine className="h-3 w-3" />{zh ? "查看签名" : "Signature"}</a>}
                {cr.drawingFileId && <a href={dl(cr.drawingFileId)} target="_blank" rel="noreferrer" data-testid="concept-drawing" className="text-[11px] text-indigo-600 inline-flex items-center gap-1"><PenLine className="h-3 w-3" />{zh ? "查看手绘修改" : "Hand-drawing"}</a>}
              </div>
            </div>
          )}
        </>
      )}

      <IntentContractCard api={api} zh={zh} leadId={leadId} canEdit={canEdit} user={user} />

      {/* Share modal */}
      {share && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShare(null)} data-testid="concept-share-modal">
          <div className="bg-white rounded-xl p-5 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShare(null)} className="ml-auto block text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            <h3 className="text-base font-bold text-slate-800 mb-1">{zh ? "发送给客户确认" : "Send to client"}</h3>
            <p className="text-[11px] text-slate-400 mb-3">
              {share.channels?.length ? (zh ? `已通过 ${share.channels.map((c) => c === "sms" ? "短信" : "邮件").join("、")} 发送` : `Sent via ${share.channels.join(", ")}`)
                : (zh ? "未配置短信/邮件，请复制链接或让客户扫码" : "Copy link / scan QR")}
            </p>
            <div className="flex justify-center mb-3 bg-white p-2 rounded-lg border border-slate-100">
              <QRCodeCanvas value={share.url} size={160} data-testid="concept-qr" />
            </div>
            <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-2 py-1.5">
              <input readOnly value={share.url} className="flex-1 text-[11px] text-slate-600 bg-transparent outline-none" data-testid="concept-share-url" />
              <button onClick={() => { navigator.clipboard?.writeText(share.url); toast.success(zh ? "已复制" : "Copied"); }} data-testid="concept-copy-link" className="text-slate-500 hover:text-emerald-600"><Copy className="h-3.5 w-3.5" /></button>
              <a href={share.url} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-emerald-600"><ExternalLink className="h-3.5 w-3.5" /></a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
